import { Form2Theme } from "../../misc/Form2";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Autocomplete, Button, DialogActions, DialogContent, TextField } from "@mui/material";
import { Column } from "../../misc/Column";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { useGetList } from "react-admin";
import { type CourseTemplate } from "../../providers/courseTemplatesProvider";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { grants } from "../../backoffice.access_control";

interface SelectCourseTypeFormValues {
  courseType: string;
}

export function SelectCourseTypeForm(props: {
  createTheoryCourse: () => void;
  createB96Course: () => void;
  createASFCourse: () => void;
  createBKFCourse: (courseTemplate: CourseTemplate) => void;
}) {
  const { createTheoryCourse, createB96Course, createASFCourse, createBKFCourse } = props;
  const drivingSchool = useCurrentDrivingSchool();
  const bkfCoursesEnabled = drivingSchool.customizations.includes("bkfCourses");
  const formProps = useForm<SelectCourseTypeFormValues>({
    defaultValues: {
      courseType: "",
    },
  });
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: courseTemplates } = useGetList(
    "courseTemplates",
    {
      pagination: { page: 1, perPage: 999 },
      sort: { field: "name", order: "ASC" },
      filter: { drivingSchoolId },
    },
    { enabled: !!drivingSchoolId && !!bkfCoursesEnabled },
  );
  const courseTypeOptions: Array<{ value: string; label: string }> = [
    { value: "theoryCourse", label: "Theoriekurs" },
    { value: "b96Course", label: "B96 Kurs" },
    ...(grants.has("asfCoursesCreate") ? [{ value: "asfCourse", label: "ASF Kurs" }] : []),
    ...(courseTemplates ? courseTemplates.map((it) => ({ value: it.id, label: it.name })) : []),
  ];

  function onSubmit(data: SelectCourseTypeFormValues) {
    if (data.courseType === "theoryCourse") {
      createTheoryCourse();
    } else if (data.courseType === "b96Course") {
      createB96Course();
    } else if (data.courseType === "asfCourse") {
      createASFCourse();
    } else {
      const courseTemplateId = data.courseType;
      const courseTemplate = courseTemplates?.find((it) => it.id === courseTemplateId);
      if (!courseTemplate) {
        throw new Error(`Selected CourseTemplate ${courseTemplateId} not found`);
      }
      createBKFCourse(courseTemplate);
    }
  }

  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit)} style={{ height: "100%" }}>
          <DialogContent>
            <Column>
              <Controller
                name="courseType"
                control={formProps.control}
                rules={{ required: "Bitte wähle eine Kursart aus." }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={courseTypeOptions}
                    value={courseTypeOptions.find((it) => it.value === field.value) ?? null}
                    onChange={(_, option) => field.onChange(option?.value ?? "")}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    openOnFocus
                    autoHighlight
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Kursart"
                        autoFocus
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Column>
          </DialogContent>
          <DialogActions>
            <Button type="submit" endIcon={<NavigateNextIcon />} variant="contained">
              Weiter
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}
